import Header from "@/components/Header";
import HeroSection from "@/components/HeroSection";
import PropertyCard from "@/components/PropertyCard";
import { Property } from "@/types/property";
import API_URL from "@/config";
import axios from "axios";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import useDocumentTitle from "@/components/Helmet";

export default function Landing() {
  useDocumentTitle("Home");
  const { toast } = useToast();
  const [limit, setLimit] = useState(6);
  const [isLoading, setIsLoading] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const fetchProperties = async (data: any) => {
    setIsLoading(true);
    try {
      if (data) {
        data.limit = limit;
      }
      else {
        data = {
          limit: limit
        }
      }
      const res = await axios.get(API_URL + `/api/properties?${new URLSearchParams(data)}`)
        .then((res) => {
          setProperties(res?.data?.properties);
          setTotalProperties(res?.data?.total);
        })

    } catch (error: any) {
      toast({
        title: "Error!",
        description: error?.response?.data?.message,
      })
    }
    finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchProperties(null);
  }, [limit])

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <HeroSection onSearch={fetchProperties} />

      {/* Featured Properties Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Featured Properties</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover exceptional homes across Ireland's most desirable locations
          </p>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 h-48 rounded-lg mb-4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard key={property._id} property={property} />
            ))}
          </div>
        )}
        {properties.length === 0 ? (
          <div className="text-center mt-8 text-gray-500 font-bold">
            No property available
          </div>
        ) : (
          <>
            {properties.length !== totalProperties ? (
              <div className="text-center mt-8">
                <button
                  onClick={() => setLimit(limit + 6)}
                  className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  data-testid="button-view-all-properties"
                >
                  View All Properties
                </button>
              </div>
            ) : (
              ( properties.length > 6 && <div className="text-center mt-8">
                <button
                  onClick={() => setLimit(Math.max(6, limit - 6))}
                  className="bg-red-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700 transition-colors"
                  data-testid="button-view-all-properties"
                >
                  View Less Properties
                </button>
              </div>)
            )
            }
          </>
        )}

      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold mb-4">
                <i className="fas fa-home mr-2"></i>PropertyHub
              </div>
              <p className="text-gray-400 mb-4">
                Ireland's leading property marketplace, connecting buyers, sellers, and renters.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Properties</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Buy Properties</a></li>
                <li><a href="#" className="hover:text-white">Rent Properties</a></li>
                <li><a href="#" className="hover:text-white">Commercial Properties</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Place an Ad</a></li>
                <li><a href="#" className="hover:text-white">Property Valuation</a></li>
                <li><a href="#" className="hover:text-white">Market Reports</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Centre</a></li>
                <li><a href="#" className="hover:text-white">Contact Us</a></li>
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center">
            <p className="text-gray-400 text-sm">© 2024 PropertyHub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
